import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import OfferCard from '@/components/shared/OfferCard';
import PageBackgroundImage from '@/components/shared/PageBackgroundImage';
import CourseTerm from '@/components/shared/Term';
import Wrapper from '@/components/shared/Wrapper';
import { useLog } from '@/hooks/useLog';
import { blockedStatus } from '@/interfaces/course';
import { enTermOrigin } from '@/interfaces/term';
import { useGetLessonUpsellQuery, useGetModulesQuery } from '@/services/course';
import { useGetLessonsQuery } from '@/services/lesson';
import { useGetLessonSavedQuery } from '@/services/savedLessons';
import styled, { StyledProp, css } from '@/styled';

import ActionBar from './ActionBar';
import ChatHandler from './Chats';
import Controls from './Controls';
import Header from './Header';
import LessonArea from './LessonArea';
import Timeline from './Timeline';

function Lesson({ className }: StyledProp) {
  const { id, hash } = useParams();
  const { activity } = useLog();
  const { data } = useGetLessonsQuery(id, { refetchOnMountOrArgChange: true });
  const { isSaved } = useGetLessonSavedQuery(parseInt(id), {
    selectFromResult: ({ data }) => {
      return { isSaved: data };
    }
  });
  const modules = useGetModulesQuery({ courseHash: hash });
  const { data: upsellData, isLoading } = useGetLessonUpsellQuery(id);

  const lessonModule = useMemo(
    () => modules.data?.data.find(module => module.id === data?.id_module),
    [data?.id_module, modules.data?.data]
  );

  const currentLesson = lessonModule?.lessons.find(lesson => lesson.id.toString() === id);

  useEffect(() => {
    if (!data) return;
    const blocked = blockedStatus.includes(data.status) ? 'bloqueado(a)' : '';
    const message = `Visualizou a aula ${data.title} do curso ${data.course_title} ${blocked}`;
    activity('viewLesson', {
      message,
      courseHash: data.hash_course,
      lessonId: data.id,
      status: data.status
    });
  }, [data, activity]);

  return (
    <div className={className}>
      <CourseTerm origin={enTermOrigin.lesson} lessonId={id} />
      <PageBackgroundImage src={data?.new_image || data?.image} alt='Imagem de fundo da tela da aula' page='lesson' />
      {data?.chats && <ChatHandler chats={data?.chats} />}
      <Wrapper className='wrapper'>
        <div className='body'>
          <div className='leftContent'>
            <Header className='title' watched={isSaved} module={lessonModule} lessonTitle={data?.title} />
            <LessonArea />
            <Controls
              status={data?.status}
              isLessonWatched={currentLesson?.watched}
              nextLesson={data?.next}
              previousLesson={data?.previous}
            />
            <ActionBar
              disable_talk_to_producer={data?.disable_talk_to_producer ?? false}
              status={data?.status}
              disable_comments={data?.disable_comments ?? true}
              key={currentLesson?.id}
            />
          </div>
          <div className='rightContent'>
            <Timeline className='desktopTimeline' />
            <OfferCard data={upsellData} isLoading={isLoading} className='offer' />
          </div>
        </div>
      </Wrapper>
    </div>
  );
}

export default styled(Lesson)(
  ({ theme }) => css`
    position: relative;
    overflow: hidden;
    min-height: 100vh;

    .wrapper {
      z-index: 2;
      position: relative;
      display: flex;
      flex-direction: column;
    }

    .ant-typography.courseTitle {
      max-width: 500px;
    }

    .title {
      margin-bottom: ${theme.space.pxToRem(24)};
    }

    .leftContent {
      width: calc(100% - 300px);

      ${theme.breakpoints.down('md')} {
        width: 100%;
      }
    }

    .body {
      position: relative;
      display: flex;
      flex-direction: row;
      column-gap: ${theme.space.pxToRem(40)};
      height: 100%;

      ${theme.breakpoints.down('md')} {
        flex-direction: column;
      }
    }

    .rightContent {
      .offer {
        width: 100%;
        margin-top: ${theme.space.pxToRem(24)};
      }

      .desktopTimeline {
        ${theme.breakpoints.down('md')} {
          display: none;
        }

        width: 300px;
        max-width: 300px;
      }
    }
  `
);
